import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { env } from "@/utils/env";
import { ENDPOINTS } from "@/constants/endpoints";
import AuthService from "../auth/auth.service";

export const api = axios.create({
  baseURL: env.API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json; charset=UTF-8",
  },
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    const handleHttpError = (status: number, data: unknown) => {
      const message =
        typeof (data as { message?: unknown })?.message === "string"
          ? (data as { message?: string }).message
          : undefined;

      const errorMessages: { [key: number]: string } = {
        400: message || "Bad Request",
        404: "Resource not found.",
        500: "Internal Server Error. Please try again later.",
      };

      throw new Error(
        errorMessages[status] ||
          message ||
          "An error occurred. Please try again."
      );
    };

    const expireSession = async () => {
      const { useAuthStore } = await import("@/stores/authStore");
      useAuthStore.getState().logout(false);
    };

    const sessionExpiredError = () =>
      new Error("Your session has expired. Please log in again.");

    if (error.response) {
      const { status, data } = error.response;

      console.log({ status, data });

      // Handle 401 errors with session refresh
      if (status === 401 && !originalRequest._retry) {
        // Don't try to refresh if the failed request was the refresh endpoint itself
        if (originalRequest.url === ENDPOINTS.AUTH.REFRESH_SESSION) {
          isRefreshing = false;
          const expired = sessionExpiredError();
          processQueue(expired);
          await expireSession();
          return Promise.reject(expired);
        }

        if (isRefreshing) {
          // If already refreshing, queue this request to retry after refresh completes
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(() => {
              return api(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Attempt to refresh the session
          await AuthService.refreshSession();

          isRefreshing = false;
          processQueue(null);

          // Retry the original request with the new session
          return api(originalRequest);
        } catch {
          isRefreshing = false;
          const expired = sessionExpiredError();
          processQueue(expired);
          await expireSession();
          return Promise.reject(expired);
        }
      }

      // Any 401 that falls through (e.g. retried request still 401) should
      // also force the user to re-authenticate.
      if (status === 401) {
        const expired = sessionExpiredError();
        await expireSession();
        throw expired;
      }

      handleHttpError(status, data);
    } else if (error.request) {
      throw new Error(
        "We detected an issue with your internet connection. Please wait a little while and try again."
      );
    } else {
      throw new Error(
        error.message ||
          "We detected an issue with our server. Please try again later."
      );
    }
  }
);

export default api;
